import { useCallback, useMemo, useState } from 'react';
import { AlertTriangle, ChevronsRight, Plus, RefreshCcw, RefreshCw } from 'lucide-react';
import clsx from 'clsx';
import type { Hit, Suite, SuiteSnapshot } from '@/lib/checkpoint/types';
import { checkpointApi } from '@/lib/checkpoint/api';
import { SuiteCard } from './SuiteCard';
import { SuiteDrawer } from './SuiteDrawer';

export interface MountedRecord {
  runId: string;
  suiteId: string;
  active: boolean;
  params: Record<string, string | number | boolean>;
}

interface Props {
  serial: string | undefined;
  /** 当前 logcat tab 的 expr 中提取的包名，缺省时空。 */
  currentPackage?: string;
  /** 受控 mounted 列表 —— 与 useTabRunnerSet 同源。 */
  mounted: MountedRecord[];
  setMounted: (next: MountedRecord[] | ((prev: MountedRecord[]) => MountedRecord[])) => void;
  /** 已加载到的套件详情；用于卡片渲染。 */
  suiteCache: Record<string, Suite>;
  setSuiteCache: (next: Record<string, Suite> | ((prev: Record<string, Suite>) => Record<string, Suite>)) => void;
  /** 当前 RunnerSet snapshots（按 runId 索引）。 */
  snapshots: SuiteSnapshot[];
  /** 命中详情，按 runId 维护。 */
  hitsByRun: Map<string, Hit[]>;
  /** 当前 RunnerSet 已分配到的最大 lineId。 */
  nextLineId: number;
  /** 数据是否仍在加载（异步拉 suite 详情时）。 */
  busy?: boolean;
  /** 收起按钮回调。 */
  onClose?: () => void;
  /** 跳到某行 callback（marker 详情 → "跳到该行"）。 */
  onJumpToLine?: (lineId: number) => void;
  /**
   * 上层 Logs.tsx 的 `tab.paused` —— 用户锁住了行级视图。注意这不是 stream
   * 级的暂停（stream 仍在累计行），但用户视角上"日志看起来停了"，他点
   * 刷新会以为没匹配上。banner 在 paused=true 时显示，提示一键解冻。
   */
  paused?: boolean;
  /** 解冻视图回调（典型：onChange({ paused: false })）。 */
  onUnpause?: () => void;
}

export function CheckpointPanel({
  serial,
  currentPackage,
  mounted,
  setMounted,
  suiteCache,
  setSuiteCache,
  snapshots,
  hitsByRun,
  nextLineId,
  busy,
  onClose,
  onJumpToLine,
  paused,
  onUnpause,
}: Props) {
  const [drawerOpen, setDrawerOpen] = useState(false);

  const snapshotByRunId = useMemo(() => {
    const map = new Map<string, SuiteSnapshot>();
    for (const s of snapshots) map.set(s.runId, s);
    return map;
  }, [snapshots]);

  const handleMount = useCallback(
    (suite: Suite, params: Record<string, string | number | boolean>) => {
      const runId = `m-${Date.now()}-${Math.random().toString(16).slice(2, 6)}-${suite.id}`;
      setSuiteCache((prev) => ({ ...prev, [suite.id]: suite }));
      setMounted((prev) => {
        // 同一份规则文件只允许出现一张挂载卡：再次挂载视为"用新参数重新挂载（verdict 重置）"。
        // 直接替换而不是新增，能避免列表里出现两张同 suite 的卡片让用户困惑。
        const filtered = prev.filter((m) => m.suiteId !== suite.id);
        return [...filtered, { runId, suiteId: suite.id, params, active: true }];
      });
    },
    [setMounted, setSuiteCache],
  );

  const handleRemove = useCallback(
    (runId: string) => {
      setMounted((prev) => prev.filter((m) => m.runId !== runId));
    },
    [setMounted],
  );

  const handleToggleActive = useCallback(
    (runId: string) => {
      setMounted((prev) =>
        prev.map((m) => (m.runId === runId ? { ...m, active: !m.active } : m)),
      );
    },
    [setMounted],
  );

  /**
   * 重新拉取某个 suite 的最新 YAML、按当前 logcat 缓冲重新匹配。
   *
   * 实现策略：refetch 拿回新 Object 引用 → 写回 suiteCache，**同时**给该
   * 套件分配一个全新的 `runId`。新的 runId 让 useTabRunnerSet 走"首次添加"
   * 分支（与初始挂载、选区→规则保存共用同一条已被验证有效的代码路径）：
   *
   *   first-add: !current.has(runId) → current.add() → replay(historyLines)
   *
   * 这样能避开"引用变化触发重建"那条隐藏的 ref-tracking 路径——某些 React
   * 渲染时序下它会被忽略，导致用户点了刷新看到 verdict 被清零却没有真正
   * 回放历史日志，要等新日志才有匹配。新 runId 同时让 SuiteCard 完整
   * remount，UI 状态也是干净的。
   */
  const handleRefreshSuite = useCallback(
    async (suiteId: string): Promise<void> => {
      try {
        const detail = await checkpointApi.getSuite(suiteId);
        setSuiteCache((prev) => ({ ...prev, [suiteId]: detail.suite }));
        setMounted((prev) =>
          prev.map((m) =>
            m.suiteId === suiteId
              ? {
                  ...m,
                  runId: `m-${Date.now()}-${Math.random()
                    .toString(16)
                    .slice(2, 6)}-${suiteId}`,
                }
              : m,
          ),
        );
      } catch (e) {
        // eslint-disable-next-line no-alert
        alert(`刷新失败：${e instanceof Error ? e.message : String(e)}`);
      }
    },
    [setSuiteCache, setMounted],
  );

  const [refreshAllBusy, setRefreshAllBusy] = useState(false);
  const handleRefreshAll = useCallback(async (): Promise<void> => {
    if (mounted.length === 0 || refreshAllBusy) return;
    setRefreshAllBusy(true);
    try {
      await Promise.all(
        Array.from(new Set(mounted.map((m) => m.suiteId))).map((id) =>
          handleRefreshSuite(id),
        ),
      );
    } finally {
      setRefreshAllBusy(false);
    }
  }, [mounted, handleRefreshSuite, refreshAllBusy]);

  const handleSaveRun = useCallback(
    async (runId: string) => {
      const snap = snapshotByRunId.get(runId);
      if (!snap) return;
      const hits: Hit[] = hitsByRun.get(runId) ?? [];
      const meta = {
        runId: snap.runId,
        suiteId: snap.suiteId,
        suiteName: snap.suiteName,
        params: snap.params,
        source: 'spa-live' as const,
        serial,
        startedAt: snap.startedAt,
        finishedAt: Date.now(),
        overallState: snap.overallState,
        totalHits: hits.length,
        linesFed: snap.linesFed,
      };
      try {
        await checkpointApi.uploadRun({ meta, snapshot: snap, hits });
        // eslint-disable-next-line no-alert
        alert(`已保存 run ${runId}`);
      } catch (e) {
        // eslint-disable-next-line no-alert
        alert(`保存失败：${e instanceof Error ? e.message : String(e)}`);
      }
    },
    [hitsByRun, serial, snapshotByRunId],
  );

  const mountedSuiteIds = useMemo(() => new Set(mounted.map((m) => m.suiteId)), [mounted]);

  return (
    <aside className="flex h-full w-full min-w-0 flex-col gap-2 rounded-md border border-line bg-bg-soft p-2">
      <header className="flex items-center justify-between">
        <h2 className="text-xs font-semibold tracking-tight text-ink">检查规则</h2>
        <div className="inline-flex items-center gap-1">
          <button
            type="button"
            className="inline-flex items-center gap-1 rounded-md border border-line bg-bg-soft px-2 py-1 text-[11px] hover:bg-bg-card"
            title="打开套件抽屉：选择 / 新建 / 导入 / 编辑 / 挂载"
            onClick={() => setDrawerOpen(true)}
          >
            <Plus className="h-3 w-3" /> 挂载套件
          </button>
          {mounted.length > 0 && (
            <button
              type="button"
              className="rounded-md border border-line bg-bg-card px-1.5 py-1 text-ink-muted hover:bg-bg-soft disabled:opacity-50"
              title="拉取所有套件最新 YAML，按当前日志缓冲重新匹配一遍"
              onClick={() => void handleRefreshAll()}
              disabled={refreshAllBusy}
            >
              <RefreshCw className={clsx('h-3.5 w-3.5', refreshAllBusy && 'animate-spin')} />
            </button>
          )}
          {onClose && (
            <button
              type="button"
              className="rounded-md border border-line bg-bg-card px-1.5 py-1 text-ink-muted hover:bg-bg-soft"
              title="收起规则面板"
              onClick={onClose}
            >
              <ChevronsRight className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </header>
      {paused && (
        <div className="flex items-start gap-1.5 rounded-md border border-amber-500/40 bg-amber-500/10 px-2 py-1.5 text-[11px] text-amber-200">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <div className="flex-1">
            <div className="font-medium">行级视图已冻结</div>
            <div className="mt-0.5 text-amber-200/80">
              新行不再进入列表，但日志缓冲仍在累积。点"刷新"匹配的是当前
              缓冲，可能与你看到的最后一行不一致。
            </div>
          </div>
          {onUnpause && (
            <button
              type="button"
              className="shrink-0 rounded border border-amber-400/60 bg-amber-500/20 px-1.5 py-0.5 font-mono text-[10px] hover:bg-amber-500/30"
              onClick={onUnpause}
            >
              解冻视图
            </button>
          )}
        </div>
      )}
      <div className="flex-1 overflow-y-auto pr-0.5">
        {mounted.length === 0 ? (
          <p className="rounded-md border border-dashed border-line p-3 text-center text-[11px] text-ink-muted">
            还没挂载任何套件 —— 点上方 <span className="font-medium text-ink">挂载套件</span> 选一个，
            或在抽屉里新建 / 导入 yaml。
          </p>
        ) : (
          <div className="space-y-2">
            {mounted.map((m) => {
              const suite = suiteCache[m.suiteId];
              if (!suite) {
                return (
                  <div
                    key={m.runId}
                    className="rounded-md border border-line bg-bg-card px-2 py-1.5 text-[11px] text-ink-muted"
                  >
                    加载套件 {m.suiteId} 中…
                  </div>
                );
              }
              return (
                <SuiteCard
                  key={m.runId}
                  suite={suite}
                  snapshot={snapshotByRunId.get(m.runId)}
                  active={m.active}
                  onToggleActive={() => handleToggleActive(m.runId)}
                  onRemove={() => handleRemove(m.runId)}
                  onSaveRun={() => void handleSaveRun(m.runId)}
                  onRefresh={() => handleRefreshSuite(m.suiteId)}
                  onJumpToLine={onJumpToLine}
                />
              );
            })}
          </div>
        )}
      </div>
      <footer className="flex items-center gap-1 border-t border-line pt-1 text-[10px] text-ink-muted">
        {busy && <RefreshCcw className="h-3 w-3 animate-spin" />}
        <span>{mounted.length} 套件</span>
        <span className="ml-auto font-mono">行 {nextLineId.toLocaleString()}</span>
      </footer>
      <SuiteDrawer
        open={drawerOpen}
        currentPackage={currentPackage}
        mountedSuiteIds={mountedSuiteIds}
        onMount={handleMount}
        onClose={() => setDrawerOpen(false)}
        onChanged={() => {
          // CRUD 后清 suiteCache：已挂载卡片下一轮刷新会重新拉最新 YAML
          setSuiteCache({});
        }}
      />
    </aside>
  );
}
